import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { HOME_ROUTE, parseRoute, routePath, type HomeNav, type Route, type WorkspaceId } from './routes';

export type View = 'home' | 'map';

/** Which workspace a home-page card navigated into — each is a distinct
 * farm business-operation domain, not a data-type grouping (see
 * pages/HomePage/workspaces.ts). `'general'` is the real, working baseline
 * (today's map as it already is: imagery + survey + full toolset) and
 * `'field-photos'` focuses the Field Photos group — see SceneManager.tsx's
 * FOCUS_MAP for the actual group/folder ids each expands. `'logistics'`,
 * `'crop-growth'` and `'water-borehole'` are real, navigable workspaces
 * with NO underlying data anywhere in the system yet — MapView.tsx shows
 * an honest empty-state banner for those instead of pretending there's a
 * layer to focus.
 * `null` = not on the map (home page). The id list itself lives in
 * routes.ts because it's also the set of valid `/map/<id>` URLs. */
export type FocusGroupId = WorkspaceId | null;

interface NavigationContextValue {
  view: View;
  focusGroupId: FocusGroupId;
  /** Which home-page section is showing (Workspaces / Content). */
  homeNav: HomeNav;
  /** Bumped every time the map view is entered (a card click, or
   * back/forward landing on a /map/… URL), even re-selecting the same
   * workspace — lets SceneManager re-apply the focused collapse state each
   * time the map is entered, not just the first time a given id is seen. */
  navToken: number;
  goHome: () => void;
  goToMap: (focus?: FocusGroupId) => void;
  setHomeNav: (nav: HomeNav) => void;
}

const NavigationContext = createContext<NavigationContextValue | null>(null);

/** Current path + query, with the query (e.g. the dev-only ?skipAuth=1)
 * carried along on every in-app navigation so it survives the whole
 * session, not just the first page. */
function withSearch(path: string): string {
  return path + window.location.search;
}

/**
 * Client-side view state between the home/workspaces page and the map
 * viewer, with the URL as the source of truth (see routes.ts for the route
 * table). Initial state comes from `location.pathname`; in-app navigation
 * pushes a history entry; back/forward re-derive state from the URL via
 * `popstate`. Lives ABOVE MapProvider in App.tsx so the map's own state
 * (drawn measurements, active layers, etc.) survives navigating home and
 * back, rather than being torn down and rebuilt.
 */
export function NavigationProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));
  const [navToken, setNavToken] = useState(0);

  // Normalize an unknown path (which parseRoute treats as home) so the
  // address bar shows '/' rather than the bogus path. Same-history-entry
  // replace, not a push.
  useEffect(() => {
    const canonical = routePath(parseRoute(window.location.pathname));
    if (window.location.pathname !== canonical) {
      window.history.replaceState(null, '', withSearch(canonical));
    }
  }, []);

  useEffect(() => {
    function onPopState() {
      const next = parseRoute(window.location.pathname);
      setRoute(next);
      if (next.view === 'map') setNavToken((t) => t + 1);
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  function navigate(next: Route) {
    const path = routePath(next);
    if (path !== window.location.pathname) window.history.pushState(null, '', withSearch(path));
    setRoute(next);
    if (next.view === 'map') setNavToken((t) => t + 1);
  }

  const value: NavigationContextValue = {
    view: route.view,
    focusGroupId: route.view === 'map' ? route.workspace : null,
    homeNav: route.view === 'home' ? route.nav : 'workspaces',
    navToken,
    goHome: () => navigate(HOME_ROUTE),
    // A bare goToMap() (no card involved) lands on the General workspace —
    // the baseline "just the map" view.
    goToMap: (focus: FocusGroupId = null) => navigate({ view: 'map', workspace: focus ?? 'general' }),
    setHomeNav: (nav: HomeNav) => navigate({ view: 'home', nav }),
  };

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation(): NavigationContextValue {
  const ctx = useContext(NavigationContext);
  if (!ctx) throw new Error('useNavigation must be used within NavigationProvider');
  return ctx;
}
